import { useCallback, useEffect, useState } from 'react'
import { getCommunicationGuidelines, upsertCommunicationGuidelines } from '../services/communicationGuidelinesApi'
import type { CommunicationGuidelinesRecord, CommunicationGuidelines } from '../types'

export function useCommunicationGuidelines(workspaceId: string) {
  const [record, setRecord] = useState<CommunicationGuidelinesRecord | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await getCommunicationGuidelines(workspaceId)
      setRecord(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load communication guidelines')
    } finally {
      setLoading(false)
    }
  }, [workspaceId])

  useEffect(() => {
    load()
  }, [load])

  const save = useCallback(async (input: CommunicationGuidelines) => {
    setSaving(true)
    setError(null)
    try {
      const updated = await upsertCommunicationGuidelines(workspaceId, input)
      setRecord(updated)
      return updated
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save communication guidelines')
      throw err
    } finally {
      setSaving(false)
    }
  }, [workspaceId])

  return {
    record,
    loading,
    saving,
    error,
    setError,
    reload: load,
    save,
  }
}
